import React, { useState } from 'react';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button'; // Import Button
import { Loader2 } from 'lucide-react'; // Import Loader icon

// Updated FormData interface
interface FormData {
  name: string;
  phone: string;
  cpf?: string; // Added CPF
  email?: string;
  address?: string;
  note?: string;
}

// Updated Props interface
interface CustomerFormProps {
  register: UseFormRegister<FormData>;
  errors: FieldErrors<FormData>;
  onCpfSearch: (cpf: string) => void; // Function to trigger search
  isSearchingCpf?: boolean; // Loading state for search
}

const CustomerForm: React.FC<CustomerFormProps> = ({ register, errors, onCpfSearch, isSearchingCpf }) => {
  // Local state for the CPF value used in the search input
  const [cpfSearchValue, setCpfSearchValue] = useState('');

  const handleSearchClick = () => {
    // Basic validation or masking could be added here
    onCpfSearch(cpfSearchValue);
  };

  return (
    <div className="p-4 border rounded-md">
      <h2 className="text-lg font-semibold mb-4">1. Informações de Contato</h2>
      <div className="space-y-4">
        {/* CPF Search Section */}
        <div>
          <Label htmlFor="cpf-search">CPF (para buscar cadastro)</Label>
          <div className="flex items-center space-x-2 mt-1">
            <Input
              id="cpf-search"
              value={cpfSearchValue}
              onChange={(e) => setCpfSearchValue(e.target.value)}
              placeholder="Digite o CPF para buscar"
              // Optional: Add masking library later
            />
            <Button
              type="button" // Prevent form submission
              onClick={handleSearchClick}
              disabled={isSearchingCpf || !cpfSearchValue}
              size="sm"
            >
              {isSearchingCpf ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Buscar'
              )}
            </Button>
          </div>
           {/* Hidden input registered with react-hook-form, populated after search */}
           <input type="hidden" {...register('cpf')} />
           {/* Display validation error for the actual form field if needed */}
           {errors.cpf && <p className="text-red-500 text-xs mt-1">{errors.cpf.message}</p>}
        </div>

        {/* Existing Fields */}
        <div>
          <Label htmlFor="name">Nome Completo <span className="text-red-500">*</span></Label>
          <Input
            id="name"
            {...register('name')}
            placeholder="Como consta no seu documento"
            className="mt-1"
          />
          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}
        </div>

        <div>
          <Label htmlFor="phone">Telefone (WhatsApp) <span className="text-red-500">*</span></Label>
          <Input
            id="phone"
            type="tel"
            {...register('phone')}
            placeholder="(99) 99999-9999"
            className="mt-1"
          />
          {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>}
        </div>

        <div>
          <Label htmlFor="email">Email (Opcional)</Label>
          <Input
            id="email"
            type="email"
            {...register('email')}
            placeholder="Para receber atualizações do pedido"
            className="mt-1"
          />
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
        </div>
        
        <div>
          <Label htmlFor="address">Endereço (Opcional)</Label>
          <Input
            id="address"
            {...register('address')}
            placeholder="Rua, número, bairro, cidade (para entrega)"
            className="mt-1"
          />
          {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address.message}</p>}
        </div>
        
        <div>
          <Label htmlFor="note">Observações (Opcional)</Label>
          <textarea
            id="note"
            rows={3}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"
            {...register('note')}
            placeholder="Ex: Ponto de referência, horário preferido para entrega..."
          />
          {errors.note && <p className="text-red-500 text-xs mt-1">{errors.note.message}</p>}
        </div>
      </div>
    </div>
  );
};

export default CustomerForm;
